import React, { useEffect, useRef, useState } from 'react'

import { Col, Row } from '@amzn/stencil-react-components/layout'
import { ProgressBar, ProgressBarStatus } from '@amzn/stencil-react-components/progress-bar'
import { Text } from '@amzn/stencil-react-components/text'

import { parseProgress } from 'src/pages/import-content/ImportContentModel'
import { CopyCell } from 'src/pages/module-builder/item-pool-editor/ItemPoolGenerator'
import { StatusWrapper } from 'src/pages/module-builder/SelfServeManualItemPoolBuilder/ManualItemPoolBuilderFinal'
import {
    GetProgressResponse,
    GetProgressResponseStatus,
    ItemPoolService,
} from 'src/services/item-pools/ItemPoolService'

const DELAY_MS = 1000

const DONE_MESSAGE = 'Done'

// Ten minutes: long enough for a large pool, short enough to surface a save that died without
// reporting an error.
export const MAX_POLLS = 600

export const TIMEOUT_MESSAGE = 'Timed out waiting for the item pool to be saved.'

export interface ImportedItemPoolProgressProps {
    versionId: string
    onSaved: () => void
    onError: (message: string) => void
}

interface SaveState {
    phase: 'saving' | 'saved' | 'failed'
    message: string
    progress: number
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// An import that skips Item Container creation saves its pool asynchronously: poll it one request
// at a time, showing the backend's "x of y" progress as a bar.
export const ImportedItemPoolProgress = ({
    versionId,
    onSaved,
    onError,
}: ImportedItemPoolProgressProps) => {
    const [save, setSave] = useState<SaveState>({ phase: 'saving', message: '', progress: 0 })
    // The latest callbacks, so callers need not memoize them and a re-render never restarts polling.
    const callbacks = useRef({ onSaved, onError })
    callbacks.current = { onSaved, onError }

    useEffect(() => {
        let cancelled = false

        const fail = (message: string) => {
            setSave((previous) => ({ ...previous, phase: 'failed', message: '' }))
            callbacks.current.onError(message)
        }

        const poll = async () => {
            for (let attempt = 0; attempt < MAX_POLLS; attempt++) {
                await delay(DELAY_MS)
                if (cancelled) {
                    return
                }
                let response: GetProgressResponse
                try {
                    response = await ItemPoolService.getProgress(versionId)
                } catch (e) {
                    // getProgress reports failures as IN_ERROR; should it ever reject, try again.
                    console.error('Failed to poll the imported item pool', e)
                    continue
                }
                if (cancelled) {
                    return
                }
                if (response.status === GetProgressResponseStatus.IN_PROGRESS) {
                    const parsed = parseProgress(response.message)
                    setSave((previous) => ({
                        phase: 'saving',
                        message: `Saving item pool: ${response.message}`,
                        progress: parsed ? parsed.done / parsed.total : previous.progress,
                    }))
                    continue
                }
                if (response.status === GetProgressResponseStatus.IN_ERROR) {
                    fail(response.message)
                } else {
                    setSave({ phase: 'saved', message: DONE_MESSAGE, progress: 1 })
                    callbacks.current.onSaved()
                }
                return
            }
            if (!cancelled) {
                fail(TIMEOUT_MESSAGE)
            }
        }

        void poll()
        return () => {
            cancelled = true
        }
    }, [versionId])

    return (
        <Col gridGap='S300' dataTestId='imported-item-pool-progress'>
            <StatusWrapper loading={save.phase === 'saving'} loadingMessage={save.message} />
            <ProgressBar
                progress={save.progress}
                status={
                    save.phase === 'failed'
                        ? ProgressBarStatus.Negative
                        : save.phase === 'saving'
                        ? ProgressBarStatus.Loading
                        : ProgressBarStatus.Positive
                }
                data-test-id='imported-item-pool-progress-bar'
            />
            {save.phase === 'saved' && (
                <Row alignItems='center' gridGap='S300' dataTestId='imported-item-pool-version-id'>
                    <Text>Item pool version ID: {versionId}</Text>
                    <CopyCell data={versionId} index={0} />
                </Row>
            )}
        </Col>
    )
}
